import type { SearchResult, SearchTarget } from './types';

const STORAGE_KEY = 'cara.search.recents';
/** How many rows the palette offers when opened with no query. */
export const MAX_RECENTS = 8;

export type RecentSearchEntry = {
  id: string;
  label: string;
  /** Section the row came from, shown beneath the label as its trail. */
  crumb: string;
  target: SearchTarget;
};

const isEntry = (value: unknown): value is RecentSearchEntry => {
  const entry = value as RecentSearchEntry | null;
  return (
    !!entry &&
    typeof entry.id === 'string' &&
    typeof entry.label === 'string' &&
    typeof entry.crumb === 'string' &&
    !!entry.target &&
    (entry.target.kind === 'route' || entry.target.kind === 'external')
  );
};

export const readRecents = (): RecentSearchEntry[] => {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]');
    // Validated rather than trusted: the shape of a stored entry changes when
    // sections change, and a stale one would otherwise render an undefined row.
    return Array.isArray(parsed) ? parsed.filter(isEntry).slice(0, MAX_RECENTS) : [];
  } catch {
    // Safari in private browsing throws on any localStorage access.
    return [];
  }
};

/** Moves an existing entry to the front rather than repeating it. */
export const rememberRecent = (crumb: string, result: SearchResult): RecentSearchEntry[] => {
  const entry: RecentSearchEntry = {
    id: result.id,
    label: result.label,
    crumb,
    target: result.primary.target
  };
  const next = [entry, ...readRecents().filter((existing) => existing.id !== entry.id)].slice(
    0,
    MAX_RECENTS
  );

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Recents are a nicety; losing them is not worth failing the action over.
  }

  return next;
};
